import { initReactI18next } from "react-i18next";

import { cy as cyLocale, enGB as enGBLocale } from "date-fns/locale";
import i18n from "i18next";

import cy from "./cy.json";
import en from "./en.json";
import { getIndefiniteArticle } from "./indefiniteArticle";

export const languages = [
  { key: "en", name: "English", flag: "gb", locale: enGBLocale },
  { key: "cy", name: "Cymraeg", flag: "gb wls", locale: cyLocale },
];
export const languageLookup = new Map(languages.map((l) => [l.key, l]));

const common = {
  uppercase: "$t({{key}}, uppercase)",
  lowercase: "$t({{key}}, capitalise)",
  capitalise: "$t({{key}}, capitalise)",
};

const defaultOptions = {
  resources: {
    cy: { translation: cy, common },
    en: { translation: en, common },
  },
  lng: "en",
  keySeparator: false as const,
  fallbackLng: { default: ["en"] },
  interpolation: { escapeValue: false },
  react: {
    bindI18nStore: "added",
    transKeepBasicHtmlNodesFor: ["br", "strong", "i", "p", "b", "span"],
  },
};

i18n.use(initReactI18next).init(defaultOptions);

i18n.services.formatter?.add("injectDefault", (value, lng, options) => {
  const interpolation =
    options[options.interpolationkey] ?? options.interpolationkey;
  if (interpolation) {
    return (
      i18n.getResource(
        lng ?? "en",
        options.ns,
        options[options.interpolationkey] || options.interpolationkey,
      ) || value
    );
  }
  return value;
});

i18n.services.formatter?.add("uppercase", (value) => {
  return value.toUpperCase();
});

i18n.services.formatter?.add("lowercase", (value) => {
  return value.toLowerCase();
});

i18n.services.formatter?.add("capitalise", (value) => {
  return value.charAt(0).toUpperCase() + value.slice(1);
});

type Terminology = {
  [key: (typeof languages)[number]["key"]]: {
    challenge?: string;
    challenges?: string;
    idea?: string;
    ideas?: string;
    impact?: string;
    impacts?: string;
    group?: string;
    groups?: string;
  };
};

const terminologyKeys = [
  "generic.challenge",
  "generic.challenges",
  "generic.challengeArticle",
  "generic.idea",
  "generic.ideas",
  "generic.ideaArticle",
  "generic.impact",
  "generic.impacts",
  "generic.impactArticle",
  "generic.group",
  "generic.groups",
  "generic.groupArticle",
] as const;

const originalBundles: Record<string, Record<string, string>> = { en, cy };

/** Restores stock generic.* strings when a language has no custom terms. */
const restoreDefaultTerminology = (lang: string) => {
  const original = originalBundles[lang];
  if (!original) return;
  const resources: Record<string, string> = {};
  for (const key of terminologyKeys) {
    resources[key] = original[key] ?? "";
  }
  i18n.addResourceBundle(lang, "translation", resources, false, true);
};

export const setInterpolationDictionary = (terminology: Terminology = {}) => {
  const termsByLanguage = terminology ?? {};
  // Visit every language so an empty dictionary clears the previous names.
  const languageKeys = new Set<string>([
    ...languages.map((language) => language.key),
    ...Object.keys(termsByLanguage),
  ]);

  languageKeys.forEach((lang) => {
    const interpolationVariables =
      termsByLanguage[lang as keyof Terminology] ?? {};
    const hasTerms = Object.values(interpolationVariables).some(
      (term) => !!term,
    );
    if (!hasTerms) {
      restoreDefaultTerminology(lang);
      return;
    }

    const challengeOne =
      interpolationVariables.challenge ||
      i18n.getResource(lang, "translation", "generic.challengeDefault");
    const challengeOther =
      interpolationVariables.challenges ||
      i18n.getResource(lang, "translation", "generic.challengesDefault");
    const challengeArticle = getIndefiniteArticle(challengeOne);

    const ideaOne =
      interpolationVariables.idea ||
      i18n.getResource(lang, "translation", "generic.ideaDefault");
    const ideaOther =
      interpolationVariables.ideas ||
      i18n.getResource(lang, "translation", "generic.ideasDefault");
    const ideaArticle = getIndefiniteArticle(ideaOne);

    const impactOne =
      interpolationVariables.impact ||
      i18n.getResource(lang, "translation", "generic.impactDefault");
    const impactOther =
      interpolationVariables.impacts ||
      i18n.getResource(lang, "translation", "generic.impactsDefault");
    const impactArticle = getIndefiniteArticle(impactOne);

    const groupOne =
      interpolationVariables.group ||
      i18n.getResource(lang, "translation", "generic.groupDefault");
    const groupOther =
      interpolationVariables.groups ||
      i18n.getResource(lang, "translation", "generic.groupsDefault");
    const groupArticle = getIndefiniteArticle(groupOne);

    const resources = {
      "generic.challenge": challengeOne,
      "generic.challenges": challengeOther,
      "generic.challengeArticle": challengeArticle,
      "generic.idea": ideaOne,
      "generic.ideas": ideaOther,
      "generic.ideaArticle": ideaArticle,
      "generic.impact": impactOne,
      "generic.impacts": impactOther,
      "generic.impactArticle": impactArticle,
      "generic.group": groupOne,
      "generic.groups": groupOther,
      "generic.groupArticle": groupArticle,
    };

    i18n.addResourceBundle(lang, "translation", resources, false, true);
  });
};

export { i18n };
